import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { CustomIcon } from "components/icon/customIcon.component";
import { StackingZIndexContext } from "components/designV2/stacking/stackingZIndexContext";
import { ZIndex } from "config/zIndex";
import { useTranslation } from "react-i18next";

export type ModalSize =
  | "compact"
  | "small"
  | "medium"
  | "large"
  | "mediumSlim"
  | "largeSlim";

export interface ModalProps {
  label: string;
  size?: ModalSize;
  showCloseButton?: boolean;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  iconName?: string;
  variant?: "default" | "confirm";
  confirmOnClose?: boolean;
  confirmText?: string;
  confirmLabel?: string;
  allowContentOverflow?: boolean;
  centerText?: boolean;
  autoFocusContent?: boolean;
}

const SIZE_MAP: Record<ModalSize, { width: number; minHeight: number }> = {
  compact: { width: 420, minHeight: 0 },
  small: { width: 520, minHeight: 520 },
  medium: { width: 1000, minHeight: 650 },
  large: { width: 1000, minHeight: 880 },
  mediumSlim: { width: 680, minHeight: 0 },
  largeSlim: { width: 820, minHeight: 0 },
};

const DEFAULT_CONFIRM_LABEL_KEY = "DesignComponents.Close";
const DEFAULT_CONFIRM_TEXT_KEY = "DesignComponents.ConfirmCloseBody";
const DEFAULT_CONFIRM_TITLE_KEY = "DesignComponents.ConfirmCloseTitle";

let modalId = 0;

/** Modal shell with header, content, and optional footer. */
export const Modal = ({
  label,
  size = "medium",
  showCloseButton = true,
  variant = "default",
  iconName,
  confirmOnClose = false,
  confirmText,
  confirmLabel,
  allowContentOverflow = false,
  centerText = false,
  autoFocusContent = true,
  isOpen,
  onClose,
  children,
  footer,
}: ModalProps): JSX.Element | null => {
  const sizeConfig = SIZE_MAP[size];
  const isConfirm = variant === "confirm";
  const titleIdRef = useRef<string>();
  const contentIdRef = useRef<string>();
  const confirmTitleIdRef = useRef<string>();
  if (!titleIdRef.current || !contentIdRef.current) {
    modalId += 1;
    titleIdRef.current = `modal-title-${modalId}`;
    contentIdRef.current = `modal-content-${modalId}`;
    confirmTitleIdRef.current = `modal-confirm-title-${modalId}`;
  }
  const titleId = titleIdRef.current;
  const contentId = contentIdRef.current;
  const confirmTitleId = confirmTitleIdRef.current;
  const cardRef = useRef<HTMLDivElement | null>(null);
  const confirmCardRef = useRef<HTMLDivElement | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    if (!autoFocusContent) {
      cardRef.current?.focus();
      return;
    }
    const focusable = cardRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  }, [isOpen, autoFocusContent]);

  useEffect(() => {
    if (!isConfirmOpen) return;
    const focusable = confirmCardRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  }, [isConfirmOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      if (isConfirmOpen) {
        setIsConfirmOpen(false);
        return;
      }
      if (confirmOnClose) {
        setIsConfirmOpen(true);
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [confirmOnClose, isConfirmOpen, isOpen, onClose]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Tab") return;
    const focusables = cardRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleConfirmKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ): void => {
    if (event.key !== "Tab") return;
    const focusables = confirmCardRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (): void => {
    if (isConfirmOpen) return;
    if (confirmOnClose) {
      setIsConfirmOpen(true);
      return;
    }
    onClose();
  };

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
  };

  const handleRequestClose = (): void => {
    if (confirmOnClose) {
      setIsConfirmOpen(true);
      return;
    }
    onClose();
  };

  const handleConfirmClose = (): void => {
    setIsConfirmOpen(false);
    onClose();
  };

  const handleCancelConfirm = (): void => {
    setIsConfirmOpen(false);
  };

  const handleConfirmOverlayClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    event.stopPropagation();
    setIsConfirmOpen(false);
  };

  return (
    <StackingZIndexContext.Provider value={ZIndex.modalOverlay}>
      <Overlay
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={handleOverlayClick}
      >
        <Card
          ref={cardRef}
          tabIndex={-1}
          $width={sizeConfig.width}
          $minHeight={sizeConfig.minHeight}
          $allowContentOverflow={allowContentOverflow}
          onKeyDown={handleKeyDown}
          onClick={handleCardClick}
          aria-describedby={contentId}
        >
          {isConfirm ? (
            <>
              <ConfirmHeader>
                {iconName && (
                  <ConfirmIcon>
                    <CustomIcon
                      name={iconName}
                      size="144px"
                      color={ColorV2.Blue4}
                    />
                  </ConfirmIcon>
                )}
                <Title id={titleId} $centered>
                  {label}
                </Title>
              </ConfirmHeader>
              <Content
                id={contentId}
                $centered
                $confirm
                $allowContentOverflow={allowContentOverflow}
              >
                <ConfirmText>{children}</ConfirmText>
              </Content>
              {footer && <Footer $centered>{footer}</Footer>}
            </>
          ) : (
            <>
              <Header>
                <Title id={titleId} $centered={centerText}>
                  {label}
                </Title>
                {showCloseButton && (
                  <CloseButtonSlot>
                    <SecondaryButton
                      iconName="v2/cross"
                      aria-label={t("DesignComponents.CloseModal")}
                      size="small"
                      onClick={handleRequestClose}
                    />
                  </CloseButtonSlot>
                )}
              </Header>
              <Content
                id={contentId}
                $allowContentOverflow={allowContentOverflow}
                $centerText={centerText}
              >
                {children}
              </Content>
              {footer && <Footer>{footer}</Footer>}
            </>
          )}
        </Card>
        {isConfirmOpen && (
          <ConfirmOverlay onClick={handleConfirmOverlayClick}>
            <ConfirmCard
              ref={confirmCardRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={confirmTitleId}
              onClick={handleCardClick}
              onKeyDown={handleConfirmKeyDown}
            >
              <ConfirmTitle id={confirmTitleId}>
                {t(DEFAULT_CONFIRM_TITLE_KEY)}
              </ConfirmTitle>
              <ConfirmBody>
                {confirmText ?? t(DEFAULT_CONFIRM_TEXT_KEY)}
              </ConfirmBody>
              <ConfirmActions>
                <SecondaryButton
                  label={t("DesignComponents.Cancel")}
                  onClick={handleCancelConfirm}
                />
                <PrimaryButton
                  label={confirmLabel ?? t(DEFAULT_CONFIRM_LABEL_KEY)}
                  onClick={handleConfirmClose}
                />
              </ConfirmActions>
            </ConfirmCard>
          </ConfirmOverlay>
        )}
      </Overlay>
    </StackingZIndexContext.Provider>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  z-index: ${ZIndex.modalOverlay};
  padding: 32px;

  @media (max-width: 1366px), (max-height: 900px) {
    padding: 16px;
  }
`;

const Card = styled.div<{
  $width: number;
  $minHeight: number;
  $allowContentOverflow: boolean;
}>`
  width: min(100%, ${(props) => props.$width}px);
  min-height: min(${(props) => props.$minHeight}px, calc(100vh - 64px));
  max-height: calc(100vh - 64px);
  background: ${ColorV2.White};
  border-radius: 16px;
  border: 1.5px solid ${ColorV2.Blue5};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: ${(props) => (props.$allowContentOverflow ? "visible" : "hidden")};

  @media (max-width: 1366px), (max-height: 900px) {
    max-height: calc(100vh - 32px);
  }
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: flex-start;
  padding: 32px 24px 0;
  gap: 16px;
`;

const Title = styled.h3<{ $centered?: boolean }>`
  ${TypographyV2.h3};
  color: ${ColorV2.Blue1};
  margin: 10px 0 0;
  min-width: 0;
  white-space: normal;
  text-align: ${(props) => (props.$centered ? "center" : "left")};
  width: ${(props) => (props.$centered ? "100%" : "auto")};
  min-width: 0;
  flex: ${(props) => (props.$centered ? "0 1 auto" : "1 1 auto")};
  overflow-wrap: anywhere;
`;

const CloseButtonSlot = styled.div`
  width: 176px;
  min-width: 176px;
  display: flex;
  justify-content: flex-end;
`;

const Content = styled.div<{
  $centered?: boolean;
  $confirm?: boolean;
  $allowContentOverflow: boolean;
  $centerText?: boolean;
}>`
  padding: ${(props) => (props.$confirm ? "16px 72px 24px" : "16px 24px 32px")};
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  overflow-y: ${(props) => (props.$allowContentOverflow ? "visible" : "auto")};
  overflow-x: ${(props) =>
    props.$allowContentOverflow ? "visible" : "hidden"};
  text-align: ${(props) =>
    props.$centered || props.$centerText ? "center" : "left"};
  align-items: ${(props) => (props.$centered ? "center" : "stretch")};
`;

const Footer = styled.div<{ $centered?: boolean }>`
  padding: 16px 24px 24px;
  display: flex;
  justify-content: ${(props) => (props.$centered ? "center" : "flex-end")};
  gap: 12px;
  margin-top: auto;
`;

const ConfirmHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px 0;
  gap: 16px;
`;

const ConfirmIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
`;

const ConfirmText = styled.div`
  ${TypographyV2.b2};
  line-height: 33px;
  color: ${ColorV2.Blue4};
  text-align: center;
  max-width: 520px;
`;

const ConfirmOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  padding: 24px;
`;

const ConfirmCard = styled.div`
  width: min(100%, 420px);
  background: ${ColorV2.White};
  border-radius: 16px;
  border: 1.5px solid ${ColorV2.Blue5};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;
`;

const ConfirmTitle = styled.div`
  ${TypographyV2.h4};
  color: ${ColorV2.Blue1};
`;

const ConfirmBody = styled.div`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue4};
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`;
